import { ArrowUpIcon } from "@phosphor-icons/react";
import { FC, useEffect, useRef, useState } from "react";

const ContentPage: FC = () => {
	const [displayedText, setDisplayedText] = useState("");
	const [isVisible, setIsVisible] = useState(false);
	const [typingComplete, setTypingComplete] = useState(false);
	const [isStillInView, setIsStillInView] = useState(false);
	const sectionRef = useRef<HTMLDivElement>(null);

	const fullText =
		"Hi, I want to learn about marketing strategies for SaaS product";

	// IntersectionObserver to detect when component is in viewport
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !isVisible) {
					setIsVisible(true);
				}
				// Track if user is still viewing this section
				setIsStillInView(entry.isIntersecting);
			},
			{
				threshold: 0.2, // Trigger when 20% of the component is visible
			},
		);

		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}

		return () => {
			if (sectionRef.current) {
				observer.unobserve(sectionRef.current);
			}
		};
	}, [isVisible]);

	// Typing animation effect
	useEffect(() => {
		if (!isVisible) return;

		let currentIndex = 0;
		setDisplayedText(""); // Reset text
		setTypingComplete(false);

		const typingInterval = setInterval(() => {
			currentIndex = currentIndex + 1;
			if (currentIndex < fullText.length) {
				setDisplayedText(fullText.slice(0, currentIndex));
			} else {
				clearInterval(typingInterval);
				setTypingComplete(true);
			}
		}, 30); // 30ms per character for smooth typing effect

		return () => clearInterval(typingInterval);
	}, [isVisible, fullText]);

	// Auto-scroll to next section when typing completes
	useEffect(() => {
		if (typingComplete && isStillInView && sectionRef.current) {
			// Wait a bit after typing completes, then scroll
			const timer = setTimeout(() => {
				// Double-check user is still in view before auto-scrolling
				if (isStillInView) {
					const nextSection = sectionRef.current
						?.nextElementSibling as HTMLElement;
					if (nextSection) {
						nextSection.scrollIntoView({ behavior: "smooth" });
					}
				}
			}, 1500); // Wait 1.5 seconds after typing completes

			return () => clearTimeout(timer);
		}
	}, [typingComplete, isStillInView]);

	// Manual scroll to next section
	const scrollToNext = () => {
		const nextSection = sectionRef.current?.nextElementSibling as HTMLElement;
		if (nextSection) {
			nextSection.scrollIntoView({ behavior: "smooth" });
		}
	};

	return (
		<div
			ref={sectionRef}
			className="w-full min-h-dvh snap-start bg-white px-6 md:px-0    pt-[248px] flex justify-center"
		>
			<div className="max-w-[643px] w-full  flex flex-col gap-8 md:gap-16">
				<p className="font-ibmPlexSerif font-normal text-base !leading-[28px] md:!leading-[32px] md:text-2xl w-full text-center text-black">
					Just tell Curo what you wanna learn
				</p>

				<div className="flex flex-col w-full gap-4 pt-6 pl-6 pr-4 pb-4 border-[0.75px] border-[#E4E3E3] backdrop-blur-[32px] rounded-[24px]">
					<div className="flex flex-row items-start gap-3 w-full ">
						<img
							src="/landingpage/star.svg"
							className="md:w-4 md:h-4 w-3 h-3 my-1"
							alt="star"
						/>
						<p className="font-public-sans w-full flex-grow font-normal text-sm text-[#1F1F1F]">
							{displayedText}
							<span className="animate-pulse">|</span>
						</p>
					</div>
					<div className="flex flex-row w-full justify-end">
						<button
							onClick={scrollToNext}
							className="rounded-[8px] bg-black text-white py-2 px-3 hover:bg-gray-800 transition-colors cursor-pointer"
						>
							<ArrowUpIcon size={16}></ArrowUpIcon>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ContentPage;
